// TASK-3 REWRITE: lab product-page idioms (LAB's `Features.tsx` PillarSection/
// TextPane pattern) applied to our STAGES pipeline content. Stage 01 has no
// capture screenshot, so its visual pane is a bordered `<dl>` readout instead
// of a `Shot` figure — everything else alternates a capture figure per stage.
import { Eyebrow } from "@/components/Eyebrow";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Section, SectionHeading } from "@/components/kit";
import { cn } from "@/lib/cn";
import { STAGES } from "@/content/platform";
import type { Capture } from "@/content/platform";
import { SITE } from "@/content/site";
import capComponent from "@/assets/captures/cap-component.jpg";
import reconDroneCanvas from "@/assets/captures/recon-drone-canvas.jpg";
import reconWide from "@/assets/captures/recon-wide.jpg";
import capLibrary from "@/assets/captures/cap-library.jpg";

const CAPTURES: Record<Capture, { src: string; alt: string }> = {
  component: {
    src: capComponent,
    alt: "Components library record for a BLDC X2216 880KV motor, showing spec, interfaces, cost and its functional model.",
  },
  canvas: {
    src: reconDroneCanvas,
    alt: "System canvas wiring a drone's radio, flight controller, ESCs, motors, propellers, battery and airframe into one co-simulation.",
  },
  running: {
    src: reconWide,
    alt: "Workshop and system view during a live run, airframe driven by NVIDIA Isaac Sim rigid-body physics.",
  },
  library: {
    src: capLibrary,
    alt: "Components library catalogue, each part carrying bill-of-materials, interfaces, cost and manufacturing data.",
  },
};

/** Framed capture screenshot, matches the lab `Shot` figure treatment. */
function Shot({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-border bg-card shadow-lift">
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
      <figcaption className="border-t border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
        {caption}
      </figcaption>
    </figure>
  );
}

/** Stage 01 has no capture yet — a bordered spec readout stands in for the visual pane. */
function IntentReadout() {
  return (
    <Card className="overflow-hidden p-0">
      <dl className="divide-y divide-border font-mono text-sm">
        <div className="flex items-center justify-between gap-4 px-5 py-3.5">
          <dt className="text-muted-foreground">intent</dt>
          <dd className="text-foreground">autonomous 5″ quadcopter</dd>
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-3.5">
          <dt className="text-muted-foreground">functions</dt>
          <dd className="text-foreground">traction · sensing · compute · power</dd>
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-3.5">
          <dt className="text-muted-foreground">constraints</dt>
          <dd className="text-foreground">payload · endurance · weight</dd>
        </div>
        <div className="flex items-center justify-between gap-4 bg-primary-tint px-5 py-3.5">
          <dt className="text-muted-foreground">output</dt>
          <dd className="flex items-center gap-2 font-semibold text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            structured spec → ready
          </dd>
        </div>
      </dl>
    </Card>
  );
}

function StageRow({ stage, flip }: { stage: (typeof STAGES)[number]; flip: boolean }) {
  const capture = "capture" in stage ? CAPTURES[stage.capture] : undefined;
  return (
    <Section wash={flip ? "surface" : "plain"} bordered>
      <div className="grid items-center gap-10 md:grid-cols-[0.86fr_1.14fr]">
        <div className={cn(flip && "md:order-2")}>
          <div className="font-mono text-xs font-bold tracking-[0.2em] text-primary">{stage.num}</div>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-balance">{stage.title}</h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{stage.body}</p>
        </div>
        <div className={cn(flip && "md:order-1")}>
          {capture ? (
            <Shot src={capture.src} alt={capture.alt} caption={stage.panelTitle} />
          ) : (
            <IntentReadout />
          )}
        </div>
      </div>
    </Section>
  );
}

export default function Platform() {
  return (
    <>
      <Section wash="wash" bordered>
        <Eyebrow>The platform</Eyebrow>
        <h1 className="mt-5 max-w-3xl text-balance text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
          One continuous pipeline, from intent to a build-ready device.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          Five stages, one model. Nothing gets re-entered, re-drawn or re-verified moving between
          them — the part you pick, the system you wire and the physics that validate it are the
          same record end to end.
        </p>
      </Section>

      {STAGES.map((stage, i) => (
        <StageRow key={stage.num} stage={stage} flip={i % 2 === 1} />
      ))}

      <Section wash="wash">
        <SectionHeading
          title="See it running on your device."
          intro="Bring a real part list — we'll wire it, close the loop through Isaac Sim, and show you the physics."
        />
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Button to="/book-demo">Book a demo</Button>
          <Button href={SITE.appUrl} target="_blank" rel="noopener" variant="ghost">
            Open the Workshop
          </Button>
        </div>
      </Section>
    </>
  );
}
